import React from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../i18n/bookingCopy';
import { HostTutorBookingResponse } from '../models/Tutor';
import {
  TutorBookingChangeProposal,
  TutorBookingChangeStatus,
} from '../models/TutorBookingChange';
import {
  formatProposalDateTime,
  getTutorChangeProposalStatusColor,
  getTutorChangeProposalStatusDescription,
  proposalTimeChanged,
} from '../utils/tutorBookingChangeProposal';
import {
  getTutorBookingStatusChipColor,
  getTutorBookingStatusChipVariant,
  tutorBookingStatusLabel,
} from '../utils/tutorBookingStatus';

/** Shared action button chrome; card layout overrides flex to fill width. */
export const TUTOR_BOOKING_REQUEST_ACTION_BUTTON_SX = {
  minWidth: 96,
  minHeight: 36,
  height: 36,
  px: 2.25,
  py: 0,
  fontSize: '0.8125rem',
  fontWeight: 600,
  lineHeight: 1.2,
  letterSpacing: '0.02em',
  textTransform: 'uppercase' as const,
  borderRadius: '999px',
  boxShadow: 'none',
  whiteSpace: 'nowrap' as const,
  flex: '0 0 auto',
  '&:hover': {
    boxShadow: 'none',
  },
} as const;

export function hasPendingTutorChangeProposal(
  changeProposal?: TutorBookingChangeProposal | null,
): boolean {
  return changeProposal?.status === TutorBookingChangeStatus.PENDING_REPLY;
}

export interface TutorBookingRequestStatusCellProps {
  bookingStatus: string;
  changeProposal?: TutorBookingChangeProposal | null;
}

const changeProposalSectionLabel = (t: (key: string, defaultValue: string) => string) =>
  t('bookings.changeProposal.title', 'Schedule change');

function ChangeProposalStatusGroup({
  changeProposal,
  awaitingStudent = false,
}: {
  changeProposal: TutorBookingChangeProposal;
  awaitingStudent?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, lineHeight: 1.2 }}>
        {changeProposalSectionLabel(t)}
      </Typography>
      <Chip
        size="small"
        variant={awaitingStudent ? 'filled' : 'outlined'}
        color={
          awaitingStudent
            ? 'warning'
            : getTutorChangeProposalStatusColor(changeProposal.status)
        }
        label={
          awaitingStudent
            ? t('hostTutorBookings.changeProposal.awaitingStudentStatus', 'Awaiting student')
            : getTutorChangeProposalStatusDescription(changeProposal.status, t)
        }
      />
    </Box>
  );
}

/** Table/mobile status: booking chip plus a labeled schedule-change block when a proposal exists. */
export function TutorBookingRequestStatusCell({
  bookingStatus,
  changeProposal,
}: TutorBookingRequestStatusCellProps) {
  const { t } = useTranslation();

  const bookingChip = (
    <Chip
      size="small"
      label={tutorBookingStatusLabel(t, bookingStatus)}
      color={getTutorBookingStatusChipColor(bookingStatus)}
      variant={getTutorBookingStatusChipVariant(bookingStatus)}
    />
  );

  if (!changeProposal) {
    return bookingChip;
  }

  return (
    <Stack spacing={0} alignItems="flex-start">
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, lineHeight: 1.2 }}>
          {t('hostTutorBookings.bookingStatusLabel', 'Lesson')}
        </Typography>
        {bookingChip}
      </Box>
      <ChangeProposalStatusGroup
        changeProposal={changeProposal}
        awaitingStudent={hasPendingTutorChangeProposal(changeProposal)}
      />
    </Stack>
  );
}

export interface TutorBookingRequestWhenCellProps {
  startTime: string;
  endTime: string;
  changeProposal?: TutorBookingChangeProposal | null;
  formatDateTime: (iso: string) => string;
  timezone?: string | null;
}

export function TutorBookingRequestWhenCell({
  startTime,
  endTime,
  changeProposal,
  formatDateTime,
  timezone,
}: TutorBookingRequestWhenCellProps) {
  const { t, i18n } = useTranslation();

  if (!hasPendingTutorChangeProposal(changeProposal) || !changeProposal) {
    return (
      <>
        <Typography variant="body2">{formatDateTime(startTime)}</Typography>
        <Typography variant="caption" color="text.secondary">
          → {formatDateTime(endTime)}
        </Typography>
      </>
    );
  }

  const timeChanged = proposalTimeChanged(changeProposal);

  if (!timeChanged) {
    return (
      <>
        <Typography variant="caption" color="text.secondary" display="block">
          {t('hostTutorBookings.changeProposal.proposedLessonTime', 'Proposed lesson time')}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {formatProposalDateTime(changeProposal.newStartTime, i18n.language, timezone)}
        </Typography>
      </>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {t('hostTutorBookings.changeProposal.proposedLessonTime', 'Proposed lesson time')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textDecoration: 'line-through' }}
      >
        {formatProposalDateTime(changeProposal.oldStartTime, i18n.language, timezone)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, my: 0.25 }}>
        <ArrowForwardIcon sx={{ fontSize: 14, color: 'primary.main' }} aria-hidden />
      </Box>
      <Typography variant="body2" fontWeight={600} color="primary.main">
        {formatProposalDateTime(changeProposal.newStartTime, i18n.language, timezone)}
      </Typography>
    </Box>
  );
}

export interface TutorBookingRequestActionsCellProps {
  row: HostTutorBookingResponse;
  busy: boolean;
  processingBookingId: string | null;
  showModeration: boolean;
  canPropose: boolean;
  canCancelProposal: boolean;
  canCancelLesson?: boolean;
  acceptDisabled: boolean;
  declineDisabled: boolean;
  onAccept: (bookingId: string) => void;
  onDecline: (bookingId: string) => void;
  onPropose: (row: HostTutorBookingResponse) => void;
  onCancelProposal: (row: HostTutorBookingResponse) => void;
  onCancelLesson?: (row: HostTutorBookingResponse) => void;
  /** Card layout stacks moderation buttons and stretches them on small screens. */
  layout?: 'inline' | 'card';
}

export function TutorBookingRequestActionsCell({
  row,
  busy,
  processingBookingId,
  showModeration,
  canPropose,
  canCancelProposal,
  canCancelLesson = false,
  acceptDisabled,
  declineDisabled,
  onAccept,
  onDecline,
  onPropose,
  onCancelProposal,
  onCancelLesson,
  layout = 'inline',
}: TutorBookingRequestActionsCellProps) {
  const { t } = useTranslation();

  const hasActions =
    showModeration || canPropose || canCancelProposal || (canCancelLesson && !!onCancelLesson);
  if (!hasActions) {
    return null;
  }

  const isCard = layout === 'card';
  const buttonSx = isCard
    ? {
        ...TUTOR_BOOKING_REQUEST_ACTION_BUTTON_SX,
        flex: '1 1 0',
        minWidth: 0,
        px: 1.5,
      }
    : TUTOR_BOOKING_REQUEST_ACTION_BUTTON_SX;

  const moderationButtons = showModeration ? (
    <>
      <Button
        size="small"
        variant="contained"
        color="success"
        onClick={() => onAccept(row.bookingId)}
        disabled={busy || acceptDisabled}
        startIcon={
          processingBookingId === row.bookingId ? (
            <CircularProgress size={14} color="inherit" />
          ) : undefined
        }
        sx={{
          ...buttonSx,
          '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
        }}
      >
        {t('tutorBookings.confirmLesson', 'Confirm')}
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={() => onDecline(row.bookingId)}
        disabled={busy || declineDisabled}
        sx={{
          ...buttonSx,
          borderColor: 'error.main',
          color: 'error.main',
        }}
      >
        {t('tutorBookings.declineLesson', 'Decline')}
      </Button>
    </>
  ) : null;

  const secondaryButtons = (
    <>
      {canPropose ? (
        <Button
          size="small"
          variant={showModeration ? 'outlined' : 'contained'}
          color="primary"
          onClick={() => onPropose(row)}
          disabled={busy}
          sx={buttonSx}
        >
          {t('tutorBookings.suggestNewTime', 'New time')}
        </Button>
      ) : null}
      {canCancelProposal ? (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => onCancelProposal(row)}
          disabled={busy}
          sx={buttonSx}
        >
          {t('hostTutorBookings.changeProposal.withdraw', 'Cancel change')}
        </Button>
      ) : null}
      {canCancelLesson && onCancelLesson ? (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          onClick={() => onCancelLesson(row)}
          disabled={busy}
          sx={buttonSx}
        >
          {t('tutorBookings.cancelLessonShort', 'Cancel lesson')}
        </Button>
      ) : null}
    </>
  );

  if (isCard) {
    return (
      <Stack
        direction="row"
        flexWrap="nowrap"
        sx={{
          width: '100%',
          gap: 1.5,
        }}
      >
        {moderationButtons}
        {secondaryButtons}
      </Stack>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      {moderationButtons}
      {secondaryButtons}
    </Box>
  );
}
